/** Maps WorldState weather to per-layer cloud-shader parameters. Pure. */
import type { WeatherNow } from '../../world/weather/types';

export interface CloudLayer {
  altitudeM: number;
  /** Noise threshold: density = smoothstep(threshold, threshold + softness, noise). Lower = more cloud. */
  threshold: number;
  softness: number;
  /** Noise feature size in metres. */
  scaleM: number;
  /** Drift velocity in m/s along the ground plane (x = east, z = north), direction the wind blows TO. */
  velocity: [number, number];
}

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

const LAYERS = [
  { altitudeM: 1_500, scaleM: 2_500, speedFactor: 1, softness: 0.18 }, // cumulus
  { altitudeM: 4_000, scaleM: 6_000, speedFactor: 1.6, softness: 0.25 }, // alto
  { altitudeM: 9_000, scaleM: 12_000, speedFactor: 2.5, softness: 0.35 }, // cirrus
] as const;

export function cloudLayers(w: WeatherNow): CloudLayer[] {
  const cover = [w.cloudLow, w.cloudMid, w.cloudHigh];
  const toRad = ((w.windDirDeg + 180) * Math.PI) / 180; // wind FROM -> blows TO
  return LAYERS.map((l, i) => {
    const c = clamp01(cover[i] ?? 0);
    const speed = Math.max(0, w.windSpeedMs) * l.speedFactor;
    return {
      altitudeM: l.altitudeM,
      threshold: 0.78 - 0.68 * c,
      softness: l.softness,
      scaleM: l.scaleM,
      velocity: [Math.sin(toRad) * speed, Math.cos(toRad) * speed],
    };
  });
}

/** The cloud noise lattice repeats every CLOUD_NOISE_PERIOD units (GLSL wraps lattice coordinates). */
export const CLOUD_NOISE_PERIOD = 256;

/**
 * Drift offsets in noise units, computed in double precision on the CPU and wrapped into the noise period, so the
 * shader never multiplies a ~1e9 s animTime in float32.
 */
export function cloudOffsets(layers: CloudLayer[], animTime: number): Array<[number, number]> {
  const wrap = (v: number): number => ((v % CLOUD_NOISE_PERIOD) + CLOUD_NOISE_PERIOD) % CLOUD_NOISE_PERIOD;
  return layers.map((l) => [wrap((l.velocity[0] * animTime) / l.scaleM), wrap((l.velocity[1] * animTime) / l.scaleM)]);
}
